import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import { intervalToDuration } from 'date-fns';
import { useTheme } from 'styled-components';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

import { WEEK_DAYS, RESPONSIVE_WEEK_DAYS } from '../../../constants';

import {
  Container,
  Wrapper,
  WeekDaySchedule,
  Schedule,
  WeekDay,
  WeekDayInfo,
  WeekDayStatus,
} from './styles';
import { useAuth } from '../../../hooks/auth';
import { TableCaption } from '../TableCaption';
import { rgba } from 'polished';

type WeekDay = {
  start: string;
  end: string;
};

type Days = {
  seg?: WeekDay[];
  ter?: WeekDay[];
  qua?: WeekDay[];
  qui?: WeekDay[];
  sex?: WeekDay[];
  sab?: WeekDay[];
  dom?: WeekDay[];
};

interface WeekSchedulesProps {
  days?: {
    [weekDay: string]:
      | {
          start: string;
          end: string;
          retirada?: boolean;
          delivery?: boolean;
        }[]
      | undefined;
  };
  onEditSchedule(weekDay: string, index: number): void;
}

const WeekSchedules: React.FC<WeekSchedulesProps> = ({
  days,
  onEditSchedule,
}) => {
  const theme = useTheme();
  const { findRotinas } = useAuth();

  const [steps, setSteps] = useState<string[][]>(RESPONSIVE_WEEK_DAYS.full);
  const [breakpoint, setBreakpoint] = useState('full');
  const [currentStep, setCurrentStep] = useState(0);
  const [gridHeight, setGridHeight] = useState(50);

  const handleSelectSchedule = useCallback(
    (weekDay: string, index: number) => {
      onEditSchedule(weekDay, index);
    },
    [onEditSchedule],
  );

  const handlePrevStep = useCallback((): void => {
    if (currentStep === 0) {
      setCurrentStep(steps.length - 1);

      return;
    }

    setCurrentStep((oldState) => oldState - 1);
  }, [steps, currentStep]);

  const handleNextStep = useCallback((): void => {
    if (currentStep === steps.length - 1) {
      setCurrentStep(0);

      return;
    }

    setCurrentStep((oldState) => oldState + 1);
  }, [steps, currentStep]);

  const hasPermission = useCallback(
    (): boolean => findRotinas('grupos')?.alterar || false,
    [findRotinas],
  );

  useLayoutEffect(() => {
    const updateBreakpoint = (): void => {
      if (window.innerWidth < theme.breakpoints.lg) {
        setBreakpoint('lg');

        return;
      }

      if (window.innerWidth < theme.breakpoints.xl) {
        setBreakpoint('xl');

        return;
      }

      if (window.innerWidth < theme.breakpoints.xxl) {
        setBreakpoint('xxl');

        return;
      }

      if (window.innerWidth < theme.breakpoints.xxxl) {
        setBreakpoint('xxxl');

        return;
      }

      setBreakpoint('full');
    };

    window.addEventListener('resize', updateBreakpoint);

    updateBreakpoint();

    return () => {
      window.removeEventListener('resize', updateBreakpoint);
    };
  }, [theme]);

  useEffect(() => {
    setSteps(RESPONSIVE_WEEK_DAYS[breakpoint]);
    setCurrentStep(0);

    if (breakpoint === 'full') {
      setGridHeight(50);

      return;
    }

    setGridHeight(40);
  }, [breakpoint]);

  return (
    <Container>
      {breakpoint !== 'full' && (
        <>
          <button className="arrow left" type="button" onClick={handlePrevStep}>
            <FiChevronLeft />
          </button>
          <button
            className="arrow right"
            type="button"
            onClick={handleNextStep}
          >
            <FiChevronRight />
          </button>
        </>
      )}

      <div className='table-caption'>
        <TableCaption color='rgba(255,137,70,0.3)' caption='Retirada' />
        <TableCaption color='rgba(255,33,144,0.3)' caption='Delivery' />
        <TableCaption color={rgba(theme.colors.green, 0.3)} caption='Retirada e Delivery' />
      </div>
      
      <Wrapper>
        {steps[currentStep].map((weekDay, weekDayIndex) => {
          const weekDayKey = weekDay as keyof Days;

          let height = 0;
          let top = '0px';
          let message = 'Fechado';
          let totalHours = 0;
          let totalMinutes = 0;

          if (days && days[weekDay]) {
            days[weekDay]?.map((schedule) => {
              const startDate = new Date(`2021-01-01T${schedule.start}:00`);
              const endDate = new Date(`2021-01-01T${schedule.end}:00`);

              const { hours, minutes } = intervalToDuration({
                start: startDate,
                end: endDate,
              });

              totalHours += Number(hours);
              totalMinutes += Number(minutes);
            });

            message = `Aberto por ${totalHours}h${totalMinutes}`;
          }

          return (
            <WeekDay key={weekDay} className="first">
              <WeekDayInfo>
                <h3>{WEEK_DAYS[weekDayIndex + currentStep]}</h3>
                <WeekDayStatus isClosed={!days || !days[weekDayKey]}>
                  <span>{message}</span>
                </WeekDayStatus>
              </WeekDayInfo>
              
              <WeekDaySchedule className="week-day-schedules">
                {Array(12)
                  .fill('')
                  .map((_, index) => (
                    <div
                      className="grid"
                      key={`${weekDay}-${index}`}
                      style={{ height: gridHeight }}
                    />
                  ))}
                {days &&
                  days[weekDay] &&
                  days[weekDay]?.map((schedule, index) => {
                    const startDate = new Date(
                      `2021-01-01T${schedule.start}:00`,
                    );
                    const endDate = new Date(`2021-01-01T${schedule.end}:00`);

                    const { hours, minutes } = intervalToDuration({
                      start: startDate,
                      end: endDate,
                    });

                    height =
                      (Number(hours) + Number(minutes) / 60) * (gridHeight / 2);

                    top = `${
                      (Number(startDate.getHours()) +
                        Number(startDate.getMinutes()) / 60) *
                      (gridHeight / 2)
                    }px`;

                    return (
                      <Schedule
                        style={{ height, top }}
                        type="button"
                        key={Math.random()}
                        onClick={() => handleSelectSchedule(weekDay, index)}
                        disabled={!hasPermission()}
                        retirada={
                          typeof schedule.retirada === 'boolean'
                            ? schedule.retirada
                            : true
                        }
                        delivery={
                          typeof schedule.delivery === 'boolean'
                            ? schedule.delivery
                            : true
                        }
                      >
                        <span>
                          {schedule.start} - {schedule.end}
                        </span>
                      </Schedule>
                    );
                  })}
              </WeekDaySchedule>
            </WeekDay>
          );
        })}
      </Wrapper>
    </Container>
  );
};

export default WeekSchedules;
